import { Component } from '@theme/component';

/**
 * Tab container: mutual-exclusive panels, click switch.
 * Scroll only when the page loads with a URL hash matching a tab panel anchor.
 */
class TabContainerComponent extends Component {
  /** @type {string | null} */
  #activeTab = null;

  /** @type {boolean} */
  #hasActivated = false;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#hasActivated = false;
    window.removeEventListener('hashchange', this.#handleHashChange);
    document.removeEventListener('click', this.#handleAnchorClick);
  }

  #init() {
    if (!this.querySelector('.tab-container__nav [data-tab-index]')) return;

    this.#bindTabs();
    this.#bindHashNavigation();

    if (this.#hasActivated) return;
    this.#hasActivated = true;

    if (this.#activateFromHash(true)) return;

    const defaultTab = this.dataset.defaultTab;
    if (defaultTab && defaultTab !== 'none') {
      this.#activateDefaultTab(defaultTab);
    } else {
      this.#deactivateAll();
    }
  }

  /**
   * @param {boolean} [scrollToTarget]
   * @returns {boolean}
   */
  #activateFromHash(scrollToTarget = true) {
    if (!location.hash) return false;

    const panel = this.#getPanelFromHash(location.hash);
    if (!panel) return false;

    this.#activate(panel.dataset.tabIndex, scrollToTarget);
    return true;
  }

  /**
   * @param {string} hash
   * @returns {HTMLElement | null}
   */
  #getPanelFromHash(hash) {
    const hashId = hash.slice(1);
    if (!hashId) return null;

    const panel = document.getElementById(hashId);
    if (!(panel instanceof HTMLElement) || !panel.dataset.tabIndex) return null;
    if (!this.querySelector('.tab-container__panels')?.contains(panel)) return null;

    return panel;
  }

  #bindHashNavigation() {
    if (this.dataset.hashBound === 'true') return;

    this.dataset.hashBound = 'true';
    window.addEventListener('hashchange', this.#handleHashChange);
    document.addEventListener('click', this.#handleAnchorClick);
  }

  #handleHashChange = () => {
    this.#activateFromHash(true);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleAnchorClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;

    const panel = this.#getPanelFromHash(hash);
    if (!panel?.dataset.tabIndex) return;

    event.preventDefault();

    if (location.hash !== hash) {
      location.hash = hash;
      return;
    }

    this.#activate(panel.dataset.tabIndex, true);
  };

  /**
   * @returns {HTMLElement[]}
   */
  #getTabs() {
    return Array.from(this.querySelectorAll('.tab-container__nav [data-tab-index]')).filter(
      (tab) => tab instanceof HTMLElement
    );
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getPanels() {
    return Array.from(this.querySelectorAll('.tab-container__panels [data-tab-index]')).filter(
      (panel) => panel instanceof HTMLElement
    );
  }

  #bindTabs() {
    const nav = this.querySelector('.tab-container__nav');
    if (!(nav instanceof HTMLElement) || nav.dataset.tabsBound === 'true') return;

    nav.dataset.tabsBound = 'true';
    nav.addEventListener('click', (event) => {
      const tab = event.target instanceof Element ? event.target.closest('[data-tab-index]') : null;
      if (!(tab instanceof HTMLElement) || !nav.contains(tab)) return;

      const tabIndex = tab.dataset.tabIndex;
      if (!tabIndex) return;
      this.#activate(tabIndex);
    });

    this.addEventListener('keydown', this.#handleKeydown);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tabs = this.#getTabs();
    if (!tabs.length) return;

    const currentIndex = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    if (currentIndex < 0) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = tabs[nextIndex];
    const tabIndex = nextTab?.dataset.tabIndex;
    if (!tabIndex) return;

    this.#activate(tabIndex);
    nextTab.focus();
  };

  /**
   * @param {string} defaultTab
   */
  #activateDefaultTab(defaultTab) {
    const tabs = this.#getTabs();
    const tabExists = tabs.some((tab) => tab.dataset.tabIndex === defaultTab);

    if (tabExists) {
      this.#activate(defaultTab, false);
      return;
    }

    const firstTabIndex = tabs[0]?.dataset.tabIndex;
    if (firstTabIndex) {
      this.#activate(firstTabIndex, false);
    } else {
      this.#deactivateAll();
    }
  }

  #deactivateAll() {
    const tabs = this.#getTabs();
    const panels = this.#getPanels();

    this.#activeTab = null;
    delete this.dataset.activeTab;

    tabs.forEach((tab) => {
      tab.classList.remove('tab-container__tab--active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    panels.forEach((panel) => {
      panel.classList.remove('tab-container__panel--active');
      panel.hidden = true;
    });

    this.#syncTabSlots(null);
  }

  /**
   * @param {string} tabIndex
   * @param {boolean} [scrollToTarget]
   */
  #activate(tabIndex, scrollToTarget = false) {
    const tabs = this.#getTabs();
    const tabExists = tabs.some((tab) => tab.dataset.tabIndex === tabIndex);
    if (!tabExists) return;

    this.#activeTab = tabIndex;
    this.dataset.activeTab = tabIndex;

    const panels = this.#getPanels();

    tabs.forEach((tab) => {
      const isActive = tab.dataset.tabIndex === tabIndex;
      tab.classList.toggle('tab-container__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.tabIndex === tabIndex;
      panel.classList.toggle('tab-container__panel--active', isActive);
      panel.hidden = !isActive;
    });

    this.#syncTabSlots(tabIndex);

    if (scrollToTarget) {
      this.#scrollToAnchorPanel(tabIndex);
    }
  }

  /**
   * Activate a tab from the theme editor when a nested block is selected.
   * @param {string} tabIndex
   */
  activateTab(tabIndex) {
    this.#activate(tabIndex, false);
  }

  /**
   * @param {string | null} tabIndex
   */
  #syncTabSlots(tabIndex) {
    const slots = this.querySelectorAll('.tab-container__blocks [data-tab-slot]');

    slots.forEach((block) => {
      if (!(block instanceof HTMLElement)) return;

      const slot = block.dataset.tabSlot;
      let isVisible = false;

      if (!tabIndex) {
        isVisible = slot === 'none';
      } else {
        isVisible = slot === tabIndex && slot !== 'none';
      }

      block.classList.toggle('tab-container__slot--hidden', !isVisible);
      block.classList.toggle('tab-container__slot--visible', isVisible);
      block.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    });
  }

  /**
   * Scroll to the tab container when opened via URL hash (external deep link).
   * Panel nodes live in a hidden wrapper, so scroll the visible container instead.
   * Internal auto-generated ids (tab-panel-*) are not scrolled.
   */
  #scrollToAnchorPanel(tabIndex) {
    const panels = this.#getPanels();
    const panel = panels.find((item) => item.dataset.tabIndex === tabIndex);

    if (!panel?.id || panel.id.startsWith('tab-panel-')) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }
}

if (!customElements.get('tab-container-component')) {
  customElements.define('tab-container-component', TabContainerComponent);
}
